import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { AccountTree, Delete, MoreVert } from "@mui/icons-material";
import Tree, { RawNodeDatum } from "react-d3-tree";
import * as d3 from "d3";
import { GroupModel } from "../../../type/response/GroupManagementInitResponse";
import {
  createNodeData,
  ExpandMoreButton,
  StyledIconButton,
} from "../GroupManagement";

/**
 * 組織図の表示設定
 */
const TREE_SETTING = {
  defaultNodeSize: { x: 500, y: 120 },
  expandedNodeSize: { x: 500, y: 220 },
  separation: { siblings: 1.3, nonSiblings: 1.3 },
  extraMargin: { x: 80, y: 80 },
  nodeTranslate: { x: -200, y: -24 },
  cardHeight: 220,
};

/**
 * カスタムノード
 */
const CustomNodeElement = ({
  nodeDatum,
  handleUpdateGroupName,
  handleDeleteGroup,
  handleEditGroupChildren,
  displayData,
  allExpanded,
  expandedNodes,
  setExpandedNodes,
}: {
  nodeDatum: RawNodeDatum;
  handleUpdateGroupName: (
    groupId: string,
    newValue: string,
    error: boolean
  ) => void;
  handleDeleteGroup: (groupId: string) => void;
  handleEditGroupChildren: (parentGroupId: string) => void;
  displayData: GroupModel[];
  allExpanded: boolean;
  expandedNodes: Set<string>;
  setExpandedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<{ isError: boolean; message?: string }>({
    isError: false,
  });
  const [expanded, setExpanded] = useState(allExpanded);
  const [clickedExpandedButton, setClickedExpandedButton] = useState<
    "expanded" | "closed" | null
  >(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuItems = [
    {
      icon: <Delete />,
      action: "グループ削除",
      onClick: () => handleDeleteGroup(nodeDatum.name),
    },
    {
      icon: <AccountTree />,
      action: "子グループ追加・削除",
      onClick: () => handleEditGroupChildren(nodeDatum.name),
    },
  ];

  /** 全開閉の変更を反映 */
  useEffect(() => {
    setExpanded(allExpanded);
    setClickedExpandedButton(null);
  }, [allExpanded]);

  /** 展開ノードにフォーカス */
  useEffect(() => {
    if (clickedExpandedButton && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [clickedExpandedButton]);

  return (
    <Card sx={{ width: "100%" }} ref={cardRef}>
      <CardActions sx={{ justifyContent: "space-between" }}>
        <TextField
          value={nodeDatum.attributes?.groupName ?? ""}
          onChange={(e) => {
            let isError = false;
            if (!e.target.value) {
              isError = true;
              setError({
                isError: true,
                message: "グループ名を入力してください。",
              });
            } else if (
              displayData.some((v) => v.group.name === e.target.value)
            ) {
              isError = true;
              setError({
                isError: true,
                message: "グループ名が既に存在します。",
              });
            } else {
              setError({ isError: false });
            }
            handleUpdateGroupName(nodeDatum.name, e.target.value, isError);
          }}
          error={error.isError}
          helperText={error.message}
          slotProps={{ formHelperText: { style: { fontSize: "0.75rem" } } }}
          label="グループ名"
          size="small"
          fullWidth
        />
        <Box sx={{ display: "flex" }}>
          <ExpandMoreButton
            expanded={expanded}
            withStyled={true}
            onClick={() => {
              const newExpanded = !expanded;
              setExpanded(newExpanded);
              const newExpandedNodes = new Set<string>(expandedNodes);
              newExpanded
                ? newExpandedNodes.add(nodeDatum.name)
                : newExpandedNodes.delete(nodeDatum.name);
              setExpandedNodes(newExpandedNodes);
              setClickedExpandedButton(newExpanded ? "expanded" : "closed");
            }}
          />
          <StyledIconButton
            type="button"
            onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
          >
            <MoreVert />
          </StyledIconButton>
        </Box>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ height: TREE_SETTING.cardHeight, overflow: "auto" }}>
          {nodeDatum.attributes?.oldGroupName && (
            <Typography variant="body2">
              {`(旧 : ${nodeDatum.attributes.oldGroupName})`}
            </Typography>
          )}
          {nodeDatum.attributes?.groupUserNames && (
            <Typography variant="body2">
              {nodeDatum.attributes?.groupUserNames}
            </Typography>
          )}
        </CardContent>
      </Collapse>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Paper>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.action} disablePadding>
                <ListItemButton
                  onClick={() => {
                    item.onClick();
                    setAnchorEl(null);
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.action} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popover>
    </Card>
  );
};

/**
 * 組織図のサイズを取得
 */
const getTreeSize = (data: RawNodeDatum, expandedNodes: Set<string>) => {
  const {
    defaultNodeSize,
    expandedNodeSize,
    separation,
    extraMargin,
    nodeTranslate,
  } = TREE_SETTING;
  const nodeSize = expandedNodes.size > 0 ? expandedNodeSize : defaultNodeSize;

  const root = d3.hierarchy<RawNodeDatum>(data);
  const treeLayout = d3
    .tree<RawNodeDatum>()
    .nodeSize([nodeSize.y, nodeSize.x])
    .separation((a, b) =>
      a.parent === b.parent ? separation.siblings : separation.nonSiblings
    );
  const treeData = treeLayout(root);

  let xMin = Infinity,
    xMax = -Infinity;
  let yMin = Infinity,
    yMax = -Infinity;

  treeData.each((node) => {
    xMin = Math.min(xMin, node.x);
    xMax = Math.max(xMax, node.x);
    yMin = Math.min(yMin, node.y);
    yMax = Math.max(yMax, node.y);
  });

  return {
    width: yMax - yMin + extraMargin.x,
    height: xMax - xMin + nodeSize.y + extraMargin.y,
    offsetY: -xMin - nodeTranslate.y,
  };
};

/**
 * 組織図表示コンポーネント
 * - react-d3-treeを使用してグループ階層をツリー形式で表示する
 * - 各ノードはグループ名の編集・削除・子グループ変更ができる
 * - props: displayData (表示対象グループ一覧), handleUpdateGroupName (グループ名更新コールバック),
 *          handleDeleteGroup (グループ削除コールバック),
 *          handleEditGroupChildren (子グループ編集コールバック), allExpanded (全展開状態)
 */
export default function OrgChart({
  displayData,
  handleUpdateGroupName,
  handleDeleteGroup,
  handleEditGroupChildren,
  allExpanded,
}: {
  displayData: GroupModel[];
  handleUpdateGroupName: (
    groupId: string,
    newValue: string,
    error: boolean
  ) => void;
  handleDeleteGroup: (groupId: string) => void;
  handleEditGroupChildren: (parentGroupId: string) => void;
  allExpanded: boolean;
}) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    allExpanded
      ? new Set<string>(displayData.map(({ group }) => group.id ?? ""))
      : new Set<string>()
  );

  useEffect(() => {
    // ドラッグ機能を除外
    document.querySelectorAll(".rd3t-grabbable").forEach((el) => {
      el.classList.remove("rd3t-grabbable");
    });
    setExpandedNodes(
      allExpanded
        ? new Set<string>(displayData.map(({ group }) => group.id ?? ""))
        : new Set<string>()
    );
  }, [allExpanded, displayData]);

  /** 組織図設定をメモ化 */
  const { nodeSize, separation, extraMargin, nodeTranslate } = useMemo(() => {
    return {
      ...TREE_SETTING,
      nodeSize:
        expandedNodes.size > 0
          ? TREE_SETTING.expandedNodeSize
          : TREE_SETTING.defaultNodeSize,
    };
  }, [expandedNodes]);

  /** GroupModel配列データを階層構造に整理 */
  const data: RawNodeDatum = useMemo(() => {
    const roots = createNodeData(displayData);
    return roots.length > 0
      ? {
          name: "root",
          children: roots,
        }
      : { name: "root" };
  }, [displayData]);

  /** 組織図設定をメモ化 */
  const treeSize = useMemo(() => {
    return getTreeSize(data, expandedNodes);
  }, [expandedNodes, data]);

  return (
    <Box sx={{ width: treeSize.width, height: treeSize.height }}>
      <Tree
        data={data}
        translate={{ x: nodeTranslate.x, y: treeSize.offsetY }}
        nodeSize={nodeSize}
        separation={separation}
        pathFunc="step"
        draggable={false}
        zoomable={false}
        renderCustomNodeElement={({ nodeDatum }) => (
          <foreignObject
            width={nodeSize.x - extraMargin.x}
            height={nodeSize.y + extraMargin.y}
            x={nodeTranslate.x}
            y={nodeTranslate.y}
          >
            {nodeDatum.name === "root" ? (
              <Box />
            ) : (
              <CustomNodeElement
                nodeDatum={nodeDatum}
                handleUpdateGroupName={handleUpdateGroupName}
                handleDeleteGroup={handleDeleteGroup}
                handleEditGroupChildren={handleEditGroupChildren}
                displayData={displayData}
                allExpanded={allExpanded}
                expandedNodes={expandedNodes}
                setExpandedNodes={setExpandedNodes}
              />
            )}
          </foreignObject>
        )}
      />
    </Box>
  );
}
